/**
 * PŘEPÍNAČ FUNKCÍ — jedno místo, kde se celá funkce aplikace schová a zase ukáže.
 *
 * PROČ
 * Některé části appky jsou postavené, ale ještě se nemají ukazovat poradcům:
 * buď nejsou obsahově hotové (videa se teprve natáčejí), nebo je majitel zatím
 * nechce v provozu. Mazat je nechceme — kód, data, překlady i backend by se
 * pak musely skládat znovu. Proto se schovají jen VSTUPNÍ MÍSTA (položka menu,
 * tlačítka, akce kroků) a přímé adresy se přesměrují jinam.
 *
 * KDO ROZHODL
 * Pan Čung, 2. 10. 2026: „nemovitostní smlouvy dej pryč" a „vzdělávací videa
 * vypni, dej pryč… ze videa se budou nahrávat". Obojí VRATNĚ.
 *
 * JAK ZAPNOUT
 * Přepsat `false` na `true` u dané funkce níž. Nic jiného se měnit nemusí:
 * položky menu, tlačítka i routy se ptají na `zapnuto()` až při vykreslení.
 * Po změně jako u každé frontendové úpravy bumpnout `CACHE_VERSION`
 * v `public/service-worker.js`, jinak poradci uvidí starou verzi z cache.
 *
 * CO VYPNUTÍ DĚLÁ A CO NE
 *  - schová vstupní místa a přímou adresu přesměruje (bez záložky v liště),
 *  - NEMAŽE stránky, API, data ani slovníky a NEMĚNÍ backend. Uložené
 *    rezervační smlouvy i nahraná videa zůstávají, jen na ně nevede cesta.
 *
 * KDE SE NA PŘEPÍNAČ PTÁ
 * Vždy přes `zapnuto("klic")` — kompletní seznam ukáže
 * `grep -rn 'zapnuto("rezervacniSmlouva")' frontend/src` (a obdobně pro další klíč).
 * Test `lib/__tests__/funkce.test.js` hlídá, že žádné volání nemá překlep v klíči.
 *
 *  rezervacniSmlouva — realitní (nemovitostní) rezervační smlouva:
 *    routy /rezervacni-smlouva a /rezervacni-smlouva/:id (vypnuto → /obchody),
 *    menu Obchody → Rezervační smlouva, tlačítka na kartě nemovitosti,
 *    u zájemců a ve složce klienta, akce fází Rezervace a Kupní smlouva.
 *    POZOR: pojistné a finanční smlouvy klientů (/smlouvy/:id) s tím nemají nic
 *    společného a zůstávají vždy.
 *  vzdelavaciVidea — vzdělávací videa a kurzy (vypnuto → /akademie/produkty).
 *    Akademie (moduly, infografiky, zkouška, skripty) zůstává vždy; adminova
 *    správa videí taky, protože tudy se videa budou nahrávat.
 *  aiTlacitka — tlačítka, která volají placenou AI (Emergent kredity). Pan Čung
 *    25. 10. 2026: „ano“ — v provozu schovat, kód nechat, zapnout jde jedním
 *    řádkem. Schová: diktování do složky klienta (Voice-Fill), hlasovou
 *    poznámku ve složce, AI e-mail ve složce a v Poště (i hromadný; odpověď
 *    na e-mail pak otevře obyčejné psaní), záložku Diskuze (AI chat o
 *    klientovi), diktování v novém záznamu Diáře, AI texty v Nastavení webu,
 *    AI návrh v Námitkách a Hlasový režim /voice-field (i REC v mobilu /m).
 *    Admin stránky (/admin/voice-notes, /admin/ai-knowledge,
 *    /admin/portfolio-doctor) zůstávají — vidí je jen admin.
 *
 * PRAVIDLO PRO KÓD
 * Volat `zapnuto()` AŽ PŘI VOLÁNÍ funkce nebo při renderu, nikdy ne při načtení
 * modulu (např. do konstanty na úrovni souboru). Jinak:
 *  1) test nemůže přepínač namockovat a ověřit oba stavy,
 *  2) zapnutí by přestalo být změnou jednoho řádku.
 */
export const FUNKCE = Object.freeze({
  rezervacniSmlouva: false,
  vzdelavaciVidea: false,
  aiTlacitka: false,
});

/**
 * Je funkce zapnutá? Neznámý klíč = vypnuto (bezpečnější než ukázat něco,
 * o čem nikdo nerozhodl). Překlep v klíči chytí test, ne poradce.
 * @param {keyof typeof FUNKCE} klic
 * @returns {boolean}
 */
export function zapnuto(klic) {
  return Object.prototype.hasOwnProperty.call(FUNKCE, klic) && FUNKCE[klic] === true;
}
